import { useTranslation } from 'react-i18next'
import { Badge, Card, CardBody, CardHeader, CardTitle } from '@ludus/ui'
import type { CardPage } from '../../types.ts'

const PHASES = ['greeting', 'needs_discovery', 'presentation', 'objections', 'closing'] as const

/** QA over the five phases: `n_a` reads «not applicable» and is never 0; `insufficient_data` is its own state. */
export function QaPanel({ qa }: { qa: CardPage['qa'] }) {
  const { t } = useTranslation()
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('pages.audits.card.qa.title', { defaultValue: 'Checklist' })}</CardTitle>
      </CardHeader>
      <CardBody>
        <ul className="flex flex-col gap-1 text-sm">
          {PHASES.map((phase) => {
            const row = qa.find((r) => r.phase === phase)
            return (
              <li key={phase} className="flex flex-col gap-0.5">
                <span className="flex items-center gap-2">
                  <span className="min-w-[8rem] text-foreground">
                    {t(`pages.audits.card.qa.phase.${phase}`, { defaultValue: phase })}
                  </span>
                  {row === undefined ? (
                    <Badge tone="muted" size="sm">
                      {t('pages.audits.card.qa.notRun', { defaultValue: 'not judged yet' })}
                    </Badge>
                  ) : row.applicability === 'n_a' ? (
                    <Badge tone="muted" size="sm">
                      {t('pages.audits.card.qa.notApplicable', { defaultValue: 'not applicable' })}
                    </Badge>
                  ) : (
                    <>
                      <Badge tone={coverageTone(row.coverage)} size="sm">
                        {t(`pages.audits.card.qa.coverage.${row.coverage}`, {
                          defaultValue: row.coverage,
                        })}
                      </Badge>
                      {row.fidelityScore !== null && (
                        <span className="text-xs text-muted">{row.fidelityScore}%</span>
                      )}
                    </>
                  )}
                </span>
                {row && <span className="text-[11px] text-muted">{row.explanation}</span>}
              </li>
            )
          })}
        </ul>
      </CardBody>
    </Card>
  )
}

function coverageTone(coverage: string): 'success' | 'warning' | 'danger' | 'muted' {
  switch (coverage) {
    case 'offered':
      return 'success'
    case 'partial':
      return 'warning'
    case 'not_offered':
      return 'danger'
    default:
      return 'muted'
  }
}
